import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import {
	type ColorStop,
	generateLutFromStops,
	generatePalette,
	getGrayscaleColor,
	getGreenColor,
	getIcyBlueColor,
} from "$/modules/spectrogram/utils/colors";
import {
	spectrogramAutoThemeColorAtom,
	customAccentColorAtom,
	themePresetAtom,
} from "$/states/theme";
import { isDarkThemeAtom } from "$/states/main";
import { parseToHsl, setHue } from "polished";

const catppuccinColors = {
	"catppuccin-mocha": { accent: "#cba6f7" },
	"catppuccin-macchiato": { accent: "#c6a0f6" },
	"catppuccin-frappe": { accent: "#ca9ee6" },
	"catppuccin-latte": { accent: "#8839ef" }
};

export const spectrogramGainAtom = atomWithStorage(
	"settings_spectrogramGain",
	3.0,
);
export const spectrogramZoomAtom = atomWithStorage(
	"settings_spectrogramZoom",
	200,
);
export const spectrogramHeightAtom = atomWithStorage(
	"settings_spectrogramHeight",
	256,
);
export const spectrogramFontSizeAtom = atomWithStorage(
	"settings_spectrogramFontSize",
	18,
);
export const spectrogramAutoAdjustFontAtom = atomWithStorage(
	"settings_spectrogramAutoAdjustFont",
	false,
);
export const spectrogramLyricMaxCharsAtom = atomWithStorage(
	"settings_spectrogramLyricMaxChars",
	20,
);
export const spectrogramScrollLeftAtom = atom(0);
export const spectrogramContainerWidthAtom = atom(0);

const icyBluePalette = {
	id: "icy_blue",
	name: "Icy Blue",
	data: generatePalette(getIcyBlueColor),
};

const grayscalePalette = {
	id: "grayscale",
	name: "Gray Scale",
	data: generatePalette(getGrayscaleColor),
};

const aegisubGreenPalette = {
	id: "aegisub_green",
	name: "Green",
	data: generatePalette(getGreenColor),
};

export const predefinedPalettes = [
	icyBluePalette,
	aegisubGreenPalette,
	grayscalePalette,
];

export const selectedPaletteIdAtom = atomWithStorage<string>(
	"settings_selectedPaletteId",
	"icy_blue",
);

export const customPaletteStopsAtom = atomWithStorage<ColorStop[]>(
	"settings_customPaletteStops",
	[
		{ id: crypto.randomUUID(), pos: 0.0, color: "#000000" },
		{ id: crypto.randomUUID(), pos: 0.5, color: "#ff0000" },
		{ id: crypto.randomUUID(), pos: 1.0, color: "#ffff00" },
	],
);

export const currentPaletteAtom = atom((get) => {
	const autoThemeColor = get(spectrogramAutoThemeColorAtom);
	const themePreset = get(themePresetAtom);
	const customAccentColor = get(customAccentColorAtom);
	const isDarkTheme = get(isDarkThemeAtom);

	if (autoThemeColor) {
		let hexColor = isDarkTheme ? "#29a383" : "#30a46c"; // Radix jade/green defaults

		if (customAccentColor && customAccentColor.startsWith("#") && customAccentColor.length === 7) {
			hexColor = customAccentColor;
		} else if (themePreset !== "default" && themePreset in catppuccinColors) {
			hexColor = catppuccinColors[themePreset as keyof typeof catppuccinColors].accent;
		}

		if (hexColor) {
			try {
				const hsl = parseToHsl(hexColor);

				const startColor = setHue((hsl.hue - 15 + 360) % 360, hexColor);
				const endColor = setHue((hsl.hue + 30) % 360, hexColor);
				const midColor = hexColor;

				const stops: ColorStop[] = [
					{ id: "auto_0", pos: 0.0, color: "#000000" },
					{ id: "auto_0.3", pos: 0.3, color: startColor },
					{ id: "auto_0.6", pos: 0.6, color: midColor },
					{ id: "auto_1", pos: 1.0, color: endColor },
				];

				const paletteId = "auto_theme_" + hexColor;
				const paletteData = generateLutFromStops(stops);
				return { id: paletteId, name: "Auto Theme", data: paletteData };
			} catch (e) {
				console.error("Failed to parse auto theme color for spectrogram", e);
			}
		}
	}

	const selectedId = get(selectedPaletteIdAtom);

	if (selectedId === "custom") {
		const stops = get(customPaletteStopsAtom);

		const paletteId =
			"custom_" +
			stops.map((s) => `${s.pos.toFixed(2)}-${s.color.substring(1)}`).join("-");

		const paletteData = generateLutFromStops(stops);

		return { id: paletteId, name: "custom", data: paletteData };
	}

	const predefined = predefinedPalettes.find((p) => p.id === selectedId);
	if (predefined) {
		return predefined;
	}

	return icyBluePalette;
});

export const spectrogramHoverPxAtom = atom(0);
export const spectrogramIsHoveringAtom = atom(false);
export const spectrogramAggressiveCullingAtom = atomWithStorage(
	"settings_spectrogramAggressiveCulling",
	false,
);
export const spectrogramCullBufferPxAtom = atomWithStorage(
	"settings_spectrogramCullBufferPx",
	0,
);

export const spectrogramHoverTimeMsAtom = atom((get) => {
	const hoverPx = get(spectrogramHoverPxAtom);
	const scrollLeft = get(spectrogramScrollLeftAtom);
	const zoom = get(spectrogramZoomAtom);
	const containerWidth = get(spectrogramContainerWidthAtom);

	if (zoom <= 0) return 0;

	const clampedMouseX = Math.max(0, Math.min(hoverPx, containerWidth));
	const hoverX = scrollLeft + clampedMouseX;
	const hoverTimeS = hoverX / zoom;

	return hoverTimeS * 1000;
});
